import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Eye, EyeSlash, GoogleLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { captureInviteCode } from '../lib/inviteCode';
import rippersClubLogo from '../assets/rippers-login-logo.png';

// Single high-quality aerial tennis hero. Golden-hour overhead clay court —
// warm, premium, on-brand for "Competitive Tennis. Simplified."
// Served from Unsplash CDN with explicit width hints for retina screens.
const HERO_BASE = 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8';
const HERO_LARGE = `${HERO_BASE}?auto=format&fit=crop&w=2400&q=80`;
const HERO_SMALL = `${HERO_BASE}?auto=format&fit=crop&w=900&q=70`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const { user, hasLadderAccess, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // When the user lands here right after a successful password reset we
  // MUST suppress every auto-redirect guard below. Otherwise an authed-but-
  // stale AuthContext (e.g. a leftover token from another device) routes
  // the user to /redeem-invite or /complete-profile instead of letting them
  // sign in fresh. The flag is on `location.state` so it naturally
  // disappears on the next navigation.
  const passwordResetSuccess = !!location.state?.passwordResetSuccess;
  // Detect a `?reason=timeout` query param so we can show a clear
  // "your session timed out" banner. Set by the axios 401 interceptor and
  // the SessionTimeoutManager when expiry fires.
  const sessionTimedOut = new URLSearchParams(location.search).get('reason') === 'timeout';

  // Preload hero so the fade-in lands cleanly without a flash of unstyled BG.
  useEffect(() => {
    const img = new Image();
    img.src = HERO_LARGE;
    img.onload = () => setImgLoaded(true);
  }, []);

  useEffect(() => {
    setInviteCode(captureInviteCode(location.search));
  }, [location.search]);

  // Already authed → bypass the form (but NOT immediately after a
  // password reset — see passwordResetSuccess above).
  // Pending or denied accounts can never reach the invite/onboard flow.
  // They're parked on the pending-approval screen until an admin reviews
  // their registration.
  if (!passwordResetSuccess && user && (user.account_status === 'pending_approval' || user.account_status === 'denied')) {
    return <Navigate to="/registration-pending" replace />;
  }
  // Approved users (with or without Singles Ladder membership) land on Home.
  // The Singles Ladder is now opt-in and admin-managed; non-ladder members
  // still get the rest of the app (Events, Home, Settings).
  if (!passwordResetSuccess && user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      // Hit the backend /auth/login first to get the session token + user
      // payload, then push the result into AuthContext (which is a state
      // setter, NOT an auth API). This was the regression — earlier the new
      // LoginPage called useAuth().login(email, password) directly, which
      // stored the email STRING into the user state, blanked CompleteProfile,
      // and produced a 401 on submit because the session cookie was never
      // set.
      const res = await authAPI.login(email, password);
      login(
        res.data.user,
        res.data.player,
        res.data.has_ladder_access,
        res.data.profile_completed,
        res.data.managed_session_ids,
        res.data.managed_events,
      );
      toast.success('Welcome back!');
      const status = res.data.account_status || res.data.user?.account_status;
      // If the user arrived via an invite link (?code=…), honour that and
      // route to the invite-redemption flow even if they're already
      // approved (the invite may grant Singles Ladder membership).
      if (status === 'pending_approval' || status === 'denied') {
        navigate('/registration-pending', { replace: true });
      } else if (inviteCode) {
        navigate(`/onboard?code=${inviteCode}`, { replace: true });
      } else if (!res.data.profile_completed) {
        navigate('/complete-profile', { replace: true });
      } else {
        // Approved user (with or without ladder membership) → Home.
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Same Emergent-managed Google OAuth handoff used by OnboardPage.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#013523]" data-testid="login-page">
      {/* Hero background — fixed full-bleed, fades in once loaded so the
          dark fallback shows first instead of a blank white flash. */}
      <picture
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <source media="(min-width: 768px)" srcSet={HERO_LARGE} />
        <img
          src={HERO_SMALL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
        />
      </picture>

      {/* Multi-stop overlay: solid dark wash + soft top→bottom gradient for
          legibility, plus a subtle vignette via radial gradient at the
          edges. Tunable via Tailwind colors. */}
      <div aria-hidden="true" className="absolute inset-0 bg-black/55" />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 35%, transparent 0%, rgba(0,0,0,0.35) 80%)',
        }}
      />

      {/* Foreground */}
      <div className="relative min-h-screen flex flex-col items-center justify-center px-5 py-10">
        {/* Branding — the attached logo IS the wordmark. Render the full
            artwork as-is, with NO synthetic background, ring, or panel. */}
        <div className="w-full max-w-sm flex flex-col items-center mb-6 sm:mb-8" data-testid="login-logo">
          <img
            src={rippersClubLogo}
            alt="Rippers"
            className="w-[90%] h-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
          />
        </div>

        {/* Card */}
        <div
          className="w-full max-w-sm rounded-3xl p-6 sm:p-7 space-y-5 backdrop-blur-xl bg-white/10 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
          data-testid="login-card"
        >
          <div>
            <h2 className="font-serif text-2xl text-white tracking-tight">Sign in</h2>
            <p className="text-xs text-white/70 mt-0.5">Enter your credentials to continue.</p>
          </div>

          {passwordResetSuccess && (
            <div
              data-testid="login-reset-success-banner"
              className="bg-emerald-500/15 border border-emerald-300/40 text-emerald-50 text-sm rounded-xl px-4 py-3"
              role="status"
            >
              Password updated. Please sign in with your new password.
            </div>
          )}

          {sessionTimedOut && !passwordResetSuccess && (
            <div
              data-testid="login-session-timeout-banner"
              className="bg-amber-500/15 border border-amber-300/40 text-amber-50 text-sm rounded-xl px-4 py-3"
              role="status"
            >
              Your session timed out. Please log back in.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="login-email"
                className="text-[11px] font-sans tracking-[0.18em] uppercase text-white/70 mb-1.5 block"
              >
                Email
              </label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full bg-white/15 border border-white/25 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#c2a251]/60 focus:border-[#c2a251]/60 transition-all"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="text-[11px] font-sans tracking-[0.18em] uppercase text-white/70 mb-1.5 block"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full bg-white/15 border border-white/25 rounded-xl px-4 py-3 pr-12 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#c2a251]/60 focus:border-[#c2a251]/60 transition-all"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#015e3f] hover:bg-[#014d34] active:scale-[0.99] transition-transform text-white rounded-full px-6 py-3 font-medium shadow-[0_8px_24px_rgba(1,94,63,0.5)] disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            {/* OR divider — pure CSS, no logic. */}
            <div className="flex items-center gap-3" aria-hidden="true">
              <div className="flex-1 h-px bg-white/20" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-white/55">or</span>
              <div className="flex-1 h-px bg-white/20" />
            </div>

            {/* Google sign-in — uses the same Emergent OAuth handoff as
                OnboardPage. Solid white surface so it never blends into
                the photo or overlay on any viewport. */}
            <button
              data-testid="login-google-button"
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-white text-[#013523] hover:bg-white/90 active:scale-[0.99] transition-transform rounded-full px-6 py-3 font-medium border border-white/30 shadow-[0_8px_24px_rgba(0,0,0,0.25)] disabled:opacity-60"
            >
              <GoogleLogo size={20} weight="bold" />
              Continue with Google
            </button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-xs text-white/70 hover:text-white hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>

        <p className="mt-6 text-[12px] text-white/70 text-center max-w-sm leading-relaxed">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-white font-medium hover:underline" data-testid="login-register-link">
            Create Free Account
          </Link>
          <br />
          <span className="text-white/55">Verify by SMS and join instantly — no invite required.</span>
        </p>

        <p className="mt-3 text-[11px] text-white/55 text-center max-w-sm">
          Received an invite?{' '}
          <Link to="/join" className="text-white/85 hover:underline" data-testid="login-join-link">
            Complete your invitation
          </Link>
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-[11px] text-white/60">
          <Link to="/privacy" className="hover:text-white/90" data-testid="login-privacy-link">
            Privacy Policy
          </Link>
          <span aria-hidden="true" className="text-white/30">•</span>
          <Link to="/terms" className="hover:text-white/90" data-testid="login-terms-link">
            Terms
          </Link>
          <span aria-hidden="true" className="text-white/30">•</span>
          <Link to="/sms-terms" className="hover:text-white/90" data-testid="login-sms-terms-link">
            SMS Terms
          </Link>
        </div>

        <p
          data-testid="login-sms-disclosure"
          className="mt-5 max-w-sm text-center text-[10px] leading-relaxed text-white/45"
        >
          By providing your phone number and enabling SMS notifications, you
          agree to receive text messages related to your ladder activity,
          including challenges, match updates, and invites. Message frequency
          varies. Message and data rates may apply. Reply STOP to opt out.
        </p>
      </div>
    </div>
  );
}
